import { ANALYTICS_CONSTANTS } from '../../constants';

export const MILLISECONDS_PER_DAY = 86_400_000;

export interface UtcRange {
  readonly startDate: Date;
  readonly endDate: Date;
}

export const toUtcStartOfDay = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));

export const toUtcEndOfDay = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));

export const calculateDaysDifference = (startDate: Date, endDate: Date): number =>
  Math.floor(Math.abs(endDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY);

export const getInclusiveDayCount = (startDate: Date, endDate: Date): number =>
  calculateDaysDifference(startDate, endDate) + 1;

export const clampDailyRange = (range: UtcRange): UtcRange => {
  const { startDate, endDate } = range;

  if (getInclusiveDayCount(startDate, endDate) <= ANALYTICS_CONSTANTS.MAX_DAILY_RANGE_DAYS) {
    return range;
  }

  const clampedStart = toUtcStartOfDay(
    new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate() - (ANALYTICS_CONSTANTS.MAX_DAILY_RANGE_DAYS - 1),
        0,
        0,
        0,
        0,
      ),
    ),
  );

  return { startDate: clampedStart, endDate };
};

export const isWithinDailyLimit = (startDate: Date, endDate: Date): boolean =>
  getInclusiveDayCount(startDate, endDate) <= ANALYTICS_CONSTANTS.MAX_DAILY_RANGE_DAYS;

export const getDefaultDailyRange = (): UtcRange => {
  const now = new Date();
  const endDate = toUtcEndOfDay(now);
  const startDate = toUtcStartOfDay(
    new Date(
      now.getTime() - (ANALYTICS_CONSTANTS.DEFAULT_DAY_RANGE_DAYS - 1) * MILLISECONDS_PER_DAY,
    ),
  );

  return { startDate, endDate };
};

export const getDateWithOffset = (date: Date, offsetDays: number): Date =>
  new Date(date.getTime() + offsetDays * MILLISECONDS_PER_DAY);

export const toCalendarDate = (date?: Date): Date | undefined =>
  date ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : undefined;

export const formatUtcDate = (date?: Date, locale?: string): string => {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date);
};
